import type { SimulationStatus } from '@/api';
import Combobox, {
  type DefaultOptionType,
} from '@/components/combobox/combobox';
import { useUserRole } from '@/hooks/use-user-role';
import { useReviewerCandidates } from '@/pages/price-planning/simulation/simulation-queries';
import CopySimulationLinkButton from '@/pages/price-planning/simulation/simulation-tool-page/components/modals/copy-simulation-link-button';
import { useSimulationQueryErrorNotification } from '@/pages/price-planning/simulation/use-simulation-query-error-notification';
import {
  Button,
  HelpText,
  Label,
  Modal,
  TextArea,
} from '@lego/connect-components-react';
import { type RefObject, useState } from 'react';
import type { ReviewerAssignmentRequest } from '../../utils/simulation-types';

const MESSAGE_MAX_LENGTH = 240;

type SendToModalProps = {
  isOpen: boolean;
  simulationId: number;
  simulationStatus: SimulationStatus;
  triggerRef: RefObject<HTMLElement | null>;
  onClose: () => void;
  onConfirm: (request: ReviewerAssignmentRequest) => void;
  isSending: boolean;
};

const SendToModal = ({
  isOpen,
  simulationId,
  simulationStatus,
  triggerRef,
  onClose,
  onConfirm,
  isSending,
}: SendToModalProps) => {
  const { isGlobalPriceManager } = useUserRole();
  const text = isGlobalPriceManager ? 'Local' : 'Global';

  const [search, setSearch] = useState('');
  const {
    data: reviewers,
    isFetching,
    isError,
    error,
  } = useReviewerCandidates(simulationId, simulationStatus, search, isOpen);
  useSimulationQueryErrorNotification({
    isError: isOpen && isError,
    error,
    title: 'search reviewer candidates',
  });

  const noEligibleUsers = !isFetching && reviewers?.length === 0;

  const reviewerOptions: DefaultOptionType[] =
    reviewers?.map((reviewer) => ({
      id: reviewer.id,
      label: reviewer.name,
    })) ?? [];

  const [selected, setSelected] = useState<DefaultOptionType>();
  const [message, setMessage] = useState('');

  const handleAssign = (afterAssign: 'close' | 'add-watchers') => {
    if (!selected) return;
    onConfirm({
      assignment: {
        newAssignedToId: selected.id,
        message: message.trim() || undefined,
      },
      afterAssign,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      triggerRef={triggerRef}
      onClose={onClose}
      size="medium"
    >
      <Modal.Title>{`Assign to ${text} Reviewer`}</Modal.Title>
      <Modal.Content>
        <CopySimulationLinkButton />
        <div>
          <div>
            <Label>Assign to</Label>
            <div style={{ marginTop: '4px' }}>
              <Combobox
                options={reviewerOptions}
                selectedItems={selected ? [selected] : []}
                isLoading={isFetching}
                inputValue={search}
                placeholder="Type to search for a user"
                isSingleSelect
                size="medium"
                onSelect={(items) => {
                  const last = [...items].pop();
                  setSelected(last ?? undefined);
                }}
                onInputChange={(value) => {
                  setSearch(value);
                }}
              />
            </div>
          </div>
          {noEligibleUsers && (
            <div style={{ marginTop: '4px' }}>
              <HelpText variant="neutral">No eligible users found.</HelpText>
            </div>
          )}
        </div>
        <div>
          <Label forId="message-field">Message (optional)</Label>
          <div style={{ marginTop: '4px' }}>
            <TextArea
              id="message-field"
              value={message}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                if (e.target.value.length <= MESSAGE_MAX_LENGTH) {
                  setMessage(e.target.value);
                }
              }}
              disabled={false}
              charCounterMax={240}
              size="large"
            />
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '4px',
            }}
          >
            <span className="body-sm-regular">
              {message.length}/{MESSAGE_MAX_LENGTH}
            </span>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button
          onClick={onClose}
          aria-label="Cancel"
          appearance="ghost"
          variant="neutral"
        >
          Cancel
        </Button>
        {isGlobalPriceManager ? (
          <>
            <Button
              aria-label="Assign and close"
              disabled={!selected || isSending}
              loading={isSending}
              onClick={() => handleAssign('close')}
              appearance="outline"
            >
              Assign and Close
            </Button>
            <Button
              aria-label="Assign and add watchers"
              disabled={!selected || isSending}
              loading={isSending}
              onClick={() => handleAssign('add-watchers')}
            >
              Assign and Add Watchers
            </Button>
          </>
        ) : (
          <Button
            aria-label="Assign"
            disabled={!selected || isSending}
            loading={isSending}
            onClick={() => handleAssign('close')}
          >
            Assign
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
export default SendToModal;
